import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import TableHeader from "../../components/Tables/TableHeader";
import toast from "react-hot-toast";
import {
  attachAdminToInstitution,
  createAdmin,
  deleteAdmin,
  dettachAdminFromInstitution,
  getAdmins,
  resetAdminPassword,
  updateAdmin,
} from "../../services/adminService";
import { Form, Modal, Select, Space, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { MdLockReset } from "react-icons/md";
import Table from "../../components/Tables/Table";
import { Formik } from "formik";
import {
  institutionAdminAttachSchema,
  institutionAdminEditSchema,
  institutionAdminResetPasswordSchema,
  institutionAdminSchema,
} from "../../validators/adminValidators";
import TextInput from "../../components/Inputs/TextInput";
import PasswordInput from "../../components/Inputs/PasswordInput";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import { CiLock } from "react-icons/ci";
import FullScreenLoader from "../../components/Loaders/FullScreenLoader";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormButton from "../../components/Buttons/FormButton";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning, IoMdAttach } from "react-icons/io";
import { GoUnlink } from "react-icons/go";
import FormErrors from "../../components/Errors/FormErrors";

const AdminList = () => {
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [admins, setAdmins] = useState([]);
  const [adminToEdit, setAdminToEdit] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errs, setErrs] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [modalText, setModalText] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [adminToResetPassword, setAdminToResetPassword] = useState(null);
  const [adminToAttach, setAdminToAttach] = useState(null);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  useEffect(() => {
    loadAdmins();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = admins;

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((admin) =>
        `${admin.username} ${admin.first_name} ${admin.last_name} ${
          admin.email
        } ${admin?.institution?.name || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredAdmins(filtered);
  }, [searchTerm, admins, selectedInstitution]);

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      console.log(error);

      if (error) {
        toast.error(
          error?.response?.data?.error || "Failed to load Institutions"
        );
      }
    } finally {
      setLoading(false);
    }
  };
  const loadAdmins = async () => {
    setLoading(true);
    try {
      const { data } = await getAdmins();
      setAdmins(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load Admins");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAdminToEdit(null);
    setAdminToDelete(null);
    setErrs(null);
  };

  const onSubmit = async (payload, resetForm) => {
    if (adminToEdit) {
      onUpdate(payload, resetForm);
    } else {
      onCreate(payload, resetForm);
    }
  };

  const onCreate = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createAdmin(payload);

      if (data) {
        setAdmins((prev) => [...prev, data.data]);
      }
      toast.success("Admin created Successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Institution Admin  not created, try again!"
        );
      }
    }
  };

  const onUpdate = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalAdmins = [...admins];

      // Find the index of the admin being updated
      const adminIndex = originalAdmins.findIndex(
        (admin) => admin.id === adminToEdit.id
      );

      if (adminIndex === -1) throw new Error("Admin not found.");

      const { data } = await updateAdmin(adminToEdit.id, payload);

      originalAdmins[adminIndex] = data.data;

      setAdmins(originalAdmins);

      setAdminToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Admin updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Admin not updated, try again!");
      }
    }
  };

  const onDeleteAdmin = async () => {
    const originalAdmins = [...admins];

    const newAdmins = originalAdmins.filter(
      (admin) => admin.id !== adminToDelete.id
    );

    setAdmins(newAdmins);

    try {
      await deleteAdmin(adminToDelete?.id);

      toast.success("Admin deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Admin not deleted, try again later"
        }`
      );

      setAdmins(originalAdmins);
    } finally {
      setIsDeleteModalOpen(false);

      setAdminToDelete(null);
    }
  };

  const handleAttachModelCancel = () => {
    setIsAttachModalOpen(false);
    setAdminToAttach(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },

    {
      title: "Name",
      dataIndex: "name",
      render: (_, record) => (
        <p>
          {record?.first_name} {record?.last_name}
        </p>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Institution",
      render: (_, record) => <p>{record?.institution?.name}</p>,
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Admin">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Admin">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Reset Password">
            <button
              onClick={() => handlePasswordReset(record)}
              className=" p-2 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 transition cursor-pointer"
            >
              <MdLockReset />
            </button>
          </Tooltip>
          {record.institution === null ? (
            <Tooltip title="Attach Admin to Institution">
              <button
                onClick={() => handleAttachAdminToInstitution(record)}
                className="p-2 border border-blue-200 rounded-md text-blue-700 hover:bg-blue-50 transition cursor-pointer"
              >
                <IoMdAttach />
              </button>
            </Tooltip>
          ) : (
            <Tooltip title="Dettach Admin From Institution">
              <button
                onClick={() => handleDettachAdminToInstitution(record)}
                className="p-2 border border-red-200 rounded-md text-red-700 hover:bg-red-50 transition cursor-pointer"
              >
                <GoUnlink />
              </button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const onSubmitReset = async (payload, resetForm) => {
    if (adminToResetPassword) {
      onPasswordReset(payload, resetForm);
    }
  };

  const handleEdit = (adminData) => {
    setAdminToEdit(adminData);
    setIsModalOpen(true);
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.username?.toUpperCase()}`
    );
    setAdminToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const handlePasswordReset = (adminData) => {
    setAdminToResetPassword(adminData);
    setIsPasswordModalOpen(true);
  };

  const handlePasswordModelCancel = () => {
    setIsPasswordModalOpen(false);
    setAdminToEdit(null);
    setAdminToResetPassword(null);
  };

  const onPasswordReset = async (payload, resetForm) => {
    setErrs(null);

    try {
      await resetAdminPassword(adminToResetPassword.id, payload);

      setIsPasswordModalOpen(false);

      toast.success("Admin Password reseted Successfully");

      setAdminToResetPassword(null);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Admin Password not Reset, try again!"
        );
      }
    }
  };

  const handleAttachAdminToInstitution = (adminData) => {
    setAdminToAttach(adminData);
    setIsAttachModalOpen(true);
  };

  const handleDettachAdminToInstitution = async (adminToDettach) => {
    try {
      const originalAdmins = [...admins];

      const adminIndex = originalAdmins.findIndex(
        (admin) => admin.id === adminToDettach.id
      );

      const { data } = await dettachAdminFromInstitution(adminToDettach.id);

      if (adminIndex !== -1) {
        originalAdmins[adminIndex] = data.data;
      }

      setAdmins(originalAdmins);

      setIsAttachModalOpen(false);

      toast.success("Admin Dettached successfully");
    } catch (error) {
      const { response } = error;
      toast.error(response.data.error || "Admin not dettached!");
    }
  };

  const onSubmitAttach = async (payload, resetForm) => {
    if (adminToAttach) {
      setErrs(null);

      try {
        const originalAdmins = [...admins];

        const adminIndex = originalAdmins.findIndex(
          (admin) => admin.id === adminToAttach.id
        );

        const { data } = await attachAdminToInstitution(
          adminToAttach.id,
          payload
        );

        if (adminIndex !== -1) {
          originalAdmins[adminIndex] = data.data;
        }

        setAdmins(originalAdmins);

        setIsAttachModalOpen(false);

        toast.success("Admin Attached to Institution successfully");

        setAdminToAttach(null);

        resetForm();
      } catch (error) {
        const { response } = error;
        if (response && response.status === 422) {
          setErrs(response.data.errors);
        } else {
          toast.error(
            response.data.error || "Admin not attached to Institution!"
          );
        }
      }
    }
  };

  if (loading) return <FullScreenLoader />;

  return (
    <>
      <PageHeading title="Administrators" subtitle1="Administrators" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Administrator List"
            count={admins.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Administrator"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAdmins.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                <Select
                  multiple={false}
                  id="institutions"
                  defaultValue={selectedInstitution}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedInstitution(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Institution
                  </Select.Option>
                  {institutions &&
                    institutions.length > 0 &&
                    institutions.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {adminToEdit
                    ? "Edit Institution Administrator"
                    : "Add Institution Administrator"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
            width={900}
          >
            <Formik
              initialValues={{
                username: adminToEdit ? adminToEdit.username : "",
                email: adminToEdit ? adminToEdit.email : "",
                first_name: adminToEdit ? adminToEdit.first_name : "",
                last_name: adminToEdit ? adminToEdit.last_name : "",
                password: "",
                password_confirmation: "",
                institution_id: "",
              }}
              enableReinitialize
              validationSchema={
                adminToEdit
                  ? institutionAdminEditSchema
                  : institutionAdminSchema
              }
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
                setFieldTouched,
                touched,
              }) => {
                return (
                  <Form className="space-y-2 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {!adminToEdit && (
                      <div className="grid gap-5 grid-cols-1 md:grid-cols-1">
                        <CustomDropDownInput
                          title="Institution"
                          multipleSelection={false}
                          name="institution_id"
                          placeholder="Select an institution"
                          requiredField={true}
                          errors={errors}
                          options={institutions}
                          handleBlur={handleBlur}
                          handleChange={handleChange}
                          values={values}
                          displayLabel="name"
                          setFieldValue={setFieldValue}
                          key="institution_id"
                          showSearch={true}
                        />
                      </div>
                    )}

                    <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                      <TextInput
                        name="username"
                        title="Username"
                        placeholder="E.g. devops"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                      <TextInput
                        name="email"
                        title="Email"
                        placeholder="E.g. devops@renu.ac.ug"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>

                    <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                      <TextInput
                        name="first_name"
                        title="First Name"
                        placeholder="E.g. John"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                      <TextInput
                        name="last_name"
                        title="Last name"
                        placeholder="E.g. Doe"
                        requiredField={true}
                        errors={errors}
                        values={values}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                      />
                    </div>

                    {!adminToEdit && (
                      <>
                        <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                          <PasswordInput
                            name="password"
                            title="Password"
                            type="password"
                            placeholder="Enter password"
                            requiredField
                            icon={<CiLock size={16} />}
                            values={values}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                            errors={touched.password && errors.password}
                            touched={touched.password}
                          />

                          <PasswordInput
                            name="password_confirmation"
                            title="Confirm Password"
                            placeholder="Enter password"
                            type="password"
                            icon={<CiLock size={16} />}
                            requiredField={true}
                            errors={touched.password && errors.password}
                            values={values}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                          />
                        </div>
                      </>
                    )}

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteAdmin}
            handleCancel={handleCancel}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          {/* RESET PWD MODAL */}
          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  Reset Password
                </h2>
              </div>
            }
            open={isPasswordModalOpen}
            onCancel={handlePasswordModelCancel}
            width="50%"
            footer={false}
          >
            <Formik
              initialValues={{
                password: "",
                password_confirmation: "",
              }}
              enableReinitialize
              validationSchema={institutionAdminResetPasswordSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmitReset(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                touched,
              }) => (
                <Form className="space-y-2 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                    <PasswordInput
                      name="password"
                      title="Password"
                      type="password"
                      placeholder="Enter password"
                      requiredField
                      icon={<CiLock size={16} />}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      errors={touched.password && errors.password}
                      touched={touched.password}
                    />

                    <PasswordInput
                      name="password_confirmation"
                      title="Confirm Password"
                      placeholder="Enter password"
                      type="password"
                      icon={<CiLock size={16} />}
                      requiredField={true}
                      errors={touched.password && errors.password}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  Attach Admin To Institution
                </h2>
              </div>
            }
            open={isAttachModalOpen}
            onCancel={handleAttachModelCancel}
            width="50%"
            footer={false}
          >
            <Formik
              initialValues={{
                username: adminToAttach ? adminToAttach.username : "",
                institution_id: "",
              }}
              enableReinitialize
              validationSchema={institutionAdminAttachSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmitAttach(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
              }) => (
                <Form className="space-y-2 px-4! py-4!">
                  {errs && <FormErrors errors={errs} />}

                  <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                    <TextInput
                      name="username"
                      title="Username"
                      type="text"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      readOnly={true}
                    />
                    <CustomDropDownInput
                      title="Institution"
                      multipleSelection={false}
                      name="institution_id"
                      placeholder="Select an institution"
                      requiredField={true}
                      errors={errors}
                      options={institutions}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      values={values}
                      displayLabel="name"
                      setFieldValue={setFieldValue}
                      key="institution_id"
                      showSearch={true}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <FormCancelButton text="Cancel" onClick={handleCancel} />
                    <FormButton
                      text="Save"
                      type="primary"
                      onClick={submitForm}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default AdminList;
